import React from "react";
import { motion } from "framer-motion";
import { CloudCog, Repeat2, Layers3, Brain, Workflow, Compass } from "lucide-react";
import { useContentLibrary } from "@/data/content";

const iconMap = { CloudCog, Repeat2, Layers3, Brain, Workflow, Compass };

export const Services = () => {
  const { data } = useContentLibrary();
  const services = data.services.map((service) => ({
    ...service,
    icon: iconMap[service.icon] || Compass,
  }));

  return (
    <section id="services" className="relative py-24 md:py-32 bg-zinc-50 border-y border-zinc-200 dark:bg-zinc-900 dark:border-zinc-800">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-12 gap-8 items-end mb-14">
          <div className="col-span-12 lg:col-span-8">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">
              Core Expertise
            </span>
            <h2 className="mt-4 font-display font-black tracking-tighter text-zinc-950 dark:text-zinc-50 text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              Enterprise IT Solutions
              <br /> & Services.
            </h2>
          </div>
          <p className="col-span-12 lg:col-span-4 text-base text-zinc-600 dark:text-zinc-400 leading-relaxed">
            Nearly three decades engineering mission-critical platforms for the Fortune 100 — now powering the same teams' AI governance and cloud-native journeys.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              className="group relative bg-white border border-zinc-200 p-8 rounded-sm transition-all duration-300 hover:border-zinc-950 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(9,9,11,0.18)] dark:bg-zinc-950 dark:border-zinc-800 dark:hover:border-zinc-50"
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-[11px] tracking-widest text-zinc-400 dark:text-zinc-500">
                  / {s.no}
                </span>
                <s.icon size={24} strokeWidth={1.5} className="text-[#002FA7] dark:text-[#7C9BFF]" />
              </div>
              <h3 className="mt-8 font-display font-bold tracking-tight text-zinc-950 dark:text-zinc-50 text-xl leading-snug">
                {s.title}
              </h3>
              <p className="mt-3 text-zinc-600 dark:text-zinc-400 leading-relaxed text-sm">{s.desc}</p>

              <div className="mt-6 flex flex-wrap gap-1.5">
                {s.tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-zinc-200 text-zinc-600 bg-zinc-50 dark:border-zinc-800 dark:text-zinc-300 dark:bg-zinc-900"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
